import { useEffect, useRef } from 'react'
import Link from 'next/link'
import {
  StackedCarousel,
  ResponsiveContainer,
} from 'react-stacked-center-carousel'
import Image from 'next/image'

import * as Styles from './style'
import { FlexCol, FlexRow } from 'styles/styled'
import { IconButton } from 'shared/components/buttons/style'

import ScrollCard from './components/ScrollCard'
import Dropdown, { SideFilterWrap } from './components/dropdown'
import { useAppDispatch, useAppSelector } from 'src/logic/redux/hooks'
import {
  setPaymentMethod,
  setSelectedNFTs,
} from 'src/logic/redux/slices/nftsSlice'
import CheckIcon from 'src/assets/svgs/CheckIcon'
import { DarkChevron } from 'shared/images'
import CustomRadioInput from 'shared/components/customRadioInput'
import { WalletTypeEnum } from 'src/logic/redux/slices/userSlice'
import {
  getPricePerNftUsd,
  getTokenPrices,
} from 'src/logic/redux/slices/tokenPricesSlice'

interface ISelectedNFTsCard {
  setIsTermsConditionsApproved?: React.Dispatch<React.SetStateAction<boolean>>
  isTermsConditionsApproved: boolean
  showConfirmMintModal: boolean
}

function SelectedNFTsCard({
  setIsTermsConditionsApproved,
  isTermsConditionsApproved,
  showConfirmMintModal,
}: ISelectedNFTsCard) {
  const ref = useRef()
  const dispatch = useAppDispatch()

  const { nftReducer, userReducer, tokenPricesSlice } = useAppSelector(
    (state) => state
  )

  const { paymentMethod, selectedNFTs } = nftReducer
  const { user } = userReducer
  const { tokenPrices, loading, pricePerNftUsd } = tokenPricesSlice

  useEffect(() => {
    if (
      !loading &&
      Object.keys(tokenPrices).length === 0 &&
      selectedNFTs.length > 0
    ) {
      dispatch(getTokenPrices())
    }
  }, [selectedNFTs, showConfirmMintModal])

  useEffect(() => {
    if (!pricePerNftUsd) dispatch(getPricePerNftUsd())
  }, [])

  const handlePaymentMethodChange = (e: any) => {
    dispatch(setPaymentMethod(e.target.value))
  }

  const renderScrollCard = (item: any) => {
    return <ScrollCard nftData={item} onRemoveClick={handleCloseClick} />
  }

  const handleCloseClick = (removeObj: any) => {
    dispatch(
      setSelectedNFTs(
        selectedNFTs?.filter((item) => item?._id !== removeObj?._id)
      )
    )
  }

  const getEthereumEqv = (count: number) => {
    if (!pricePerNftUsd) return ''
    if (!tokenPrices['ethereum']) return ''
    return (
      ((pricePerNftUsd * count) / tokenPrices['ethereum']).toFixed(6) + ' ETH '
    )
  }

  return (
    <Styles.MintContainer>
      <FlexRow>
        <IconButton
          width='max-content'
          onClick={() => {
            // @ts-ignore
            ref.current?.goBack()
          }}
          disabled={selectedNFTs?.length === 1}
        >
          <Image src={DarkChevron} alt='arrow-icon' />
        </IconButton>

        {!!selectedNFTs?.length && (
          <ResponsiveContainer
            carouselRef={ref}
            render={(parentWidth, carouselRef) => {
              return (
                <StackedCarousel
                  ref={carouselRef}
                  fadeDistance={0.5}
                  slideComponent={renderScrollCard} // UI-component
                  slideWidth={parentWidth < 800 ? parentWidth - 40 : 750}
                  carouselWidth={parentWidth}
                  data={selectedNFTs}
                  maxVisibleSlide={
                    selectedNFTs.length === 2
                      ? 3
                      : selectedNFTs?.length % 2 === 0
                      ? selectedNFTs?.length - 1
                      : selectedNFTs?.length
                  }
                  useGrabCursor
                />
              )
            }}
          />
        )}
        <IconButton
          width='max-content'
          onClick={() => {
            // @ts-ignore
            ref.current?.goNext(6)
          }}
          disabled={selectedNFTs?.length === 1}
        >
          <Image src={DarkChevron} alt='arrow-icon' className='right' />
        </IconButton>
      </FlexRow>
      <FlexCol gap='36px'>
        <FlexCol gap='24px'>
          <FlexCol gap='16px'>
            <FlexRow justifyContent='space-between'>
              <Styles.Info>Each NFT</Styles.Info>
              <Styles.Value>
                {getEthereumEqv(1)} ($
                {pricePerNftUsd?.toFixed(2)})
              </Styles.Value>
            </FlexRow>
            <FlexRow justifyContent='space-between'>
              <Styles.Info>Number of NFTs</Styles.Info>
              <Styles.Value>{selectedNFTs.length}</Styles.Value>
            </FlexRow>
            <FlexRow justifyContent='space-between'>
              <Styles.Info>Total</Styles.Info>

              <Styles.Value>
                {getEthereumEqv(selectedNFTs.length)} ($
                {(selectedNFTs.length * pricePerNftUsd).toFixed(2)})
              </Styles.Value>
            </FlexRow>
          </FlexCol>

          <FlexCol gap='24px'>
            <FlexRow gap='16px' width='100%' justifyContent='space-between'>
              <Styles.Info>Payment</Styles.Info>
              <FlexRow width='fit-content'>
                {(user?.walletType === WalletTypeEnum.METAMASK ||
                  user?.walletType === WalletTypeEnum.WALLET_CONNECT) && (
                  <CustomRadioInput
                    label='Crypto'
                    id='crypto'
                    name='paymentType'
                    checked={paymentMethod === 'crypto'}
                    value={'crypto'}
                    onChange={handlePaymentMethodChange}
                  />
                )}

                <CustomRadioInput
                  label='Fiat'
                  id='fiat'
                  name='paymentType'
                  checked={paymentMethod === 'fiat'}
                  value={'fiat'}
                  onChange={handlePaymentMethodChange}
                />
              </FlexRow>
            </FlexRow>

            <FlexRow gap='8px' justifyContent='space-between'>
              <Styles.Info>Pay With</Styles.Info>

              {paymentMethod === 'crypto' ? (
                <Dropdown />
              ) : (
                <SideFilterWrap>
                  <div className='fiat'>USD</div>
                </SideFilterWrap>
              )}
            </FlexRow>
          </FlexCol>
        </FlexCol>

        <FlexRow>
          <Styles.CheckBox
            checked={true}
            onClick={() =>
              setIsTermsConditionsApproved?.(!isTermsConditionsApproved)
            }
          >
            {isTermsConditionsApproved && <CheckIcon />}
          </Styles.CheckBox>

          <Styles.Info
            as='label'
            htmlFor='terms'
            onClick={() =>
              setIsTermsConditionsApproved?.(!isTermsConditionsApproved)
            }
          >
            I agree to{' '}
            <Link
              href={'/terms-and-conditions'}
              target='_blank'
              rel='noopener noreferrer'
              className='hyper-link'
              onClick={(e) => e.stopPropagation()}
            >
              Terms & Conditions
            </Link>{' '}
            and{' '}
            <Link
              href={'/privacy-policy'}
              target='_blank'
              rel='noopener noreferrer'
              className='hyper-link'
              onClick={(e) => e.stopPropagation()}
            >
              Privacy Policy
            </Link>
          </Styles.Info>
        </FlexRow>
      </FlexCol>
    </Styles.MintContainer>
  )
}

export default SelectedNFTsCard
